import React from 'react';
import {
  IconButton,
  Popover,
  Stack,
  Typography,
  ButtonBase,
  useMediaQuery,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { styled } from '@mui/system';
import { red, grey } from '@mui/material/colors';
import { DARK } from 'lib/config';
import { useTranslation } from 'react-i18next';

import VideoModal from '../VideoModal/VideoModal';
import { useFaqVideoContext } from 'providers/FaqVideoProvider';
import { Progress } from 'components/Progress';

const Wrapper = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  '.homelink': {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '.MuiTypography-root': {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
}));

const WrapperInt = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: '0 10px',
}));

const WrapperIntMobile = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: '0 5px',
}));

const FaqView = ({ type, app }: { type: string; app: string }) => {
  const { faqVideos, faqLoading, getFaqVideos } = useFaqVideoContext();

  const matches = useMediaQuery('(max-width:599px)');

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>();
  const [selectedLink, setSelectedLink] = React.useState<any>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'launcher-popover' : undefined;
  const { t } = useTranslation();

  const handleLink = (link: any) => {
    setSelectedLink(link);
    handleClose();
  };

  const renderLinks = () => {
    if (faqLoading || !faqVideos) return <Progress />;

    if (faqVideos.length === 0)
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            padding: '24px',
            width: '350px',
          }}
        >
          <Typography>
            {t('noFaqVideos', 'No FAQ videos for this App')}
          </Typography>
        </Stack>
      );

    return faqVideos.map((el: any) => (
      <ButtonBase
        sx={{ width: '100%', display: 'block' }}
        onClick={() => {
          handleLink(el);
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            cursor: 'pointer',
            transition: 'background-color .2s',
            '&:hover': {
              backgroundColor: grey[50],
            },
            padding: '24px',
            width: '350px',
            borderBottom: `1px dotted ${grey[300]}`,
            '&:last-child': {
              borderBottom: 'none',
            },
          }}
        >
          <div style={{ paddingRight: 20, textAlign: 'left' }}>
            <Typography variant="body2" fontSize="14px">
              {el.titolo}
            </Typography>
          </div>
          <ArrowForwardIcon />
        </Stack>
      </ButtonBase>
    ));
  };

  React.useEffect(() => {
    getFaqVideos(app);
  }, [app]);

  if (faqLoading || (!faqLoading && faqVideos.length === 0)) return null;

  const WrapperComp = matches ? WrapperIntMobile : WrapperInt;

  return (
    <>
      <WrapperComp>
        <IconButton
          aria-describedby={id}
          onClick={handleClick}
          sx={{
            border: 0,
            background: open ? '#fff' : 'transparent',
          }}
        >
          <HelpOutlineIcon
            sx={{
              color: open
                ? '#1A416B'
                : type === DARK
                ? grey[50]
                : 'rgba(0, 0, 0, 0.54)',
            }}
          />
        </IconButton>
      </WrapperComp>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        // sx={{ top: '-9px' }}
      >
        <Wrapper direction="column" sx={{ padding: '16px 0 0' }}>
          <Stack direction="row" sx={{ marginBottom: 2, padding: '0 16px' }}>
            <Typography variant="h3" fontSize={'20px'}>
              {t('needHelp', 'Do you need help?')}
            </Typography>
          </Stack>
          <Stack direction="column" sx={{ overflowY: 'auto' }}>
            {renderLinks()}
          </Stack>
        </Wrapper>
      </Popover>

      <VideoModal
        open={selectedLink}
        onClose={() => {
          setSelectedLink(null);
        }}
        url={selectedLink ? selectedLink.linkYoutube : null}
      />
    </>
  );
};

export default FaqView;
